import pino from 'pino';
import type { LoggerConfig, Logger, Tracer } from '../types';
import { config as defaults } from '../config';

export class PinoLogger implements Logger {
  protected name: string;

  protected logger: pino.Logger;

  protected config: LoggerConfig;

  protected tracer: Tracer | undefined;

  constructor(name: string, config?: Partial<LoggerConfig>, logger?: pino.Logger) {
    this.name = name;
    this.config = this.normalizeConfig(config);
    this.logger = logger || pino(this.getPinoOptions(this.name, this.config));
  }

  private normalizeConfig(config: Partial<LoggerConfig> = {}): LoggerConfig {
    return {
      disabled: config.disabled ?? defaults.disabled,
      level: config.level || defaults.level,
      datadog: config.datadog || defaults.datadog,
      mixin: config.mixin,
      forceConsole: config.forceConsole ?? defaults.forceConsole,
      transport: config.transport
    };
  }

  protected getPinoOptions(name: string, config: LoggerConfig): pino.LoggerOptions {
    const mixin = this.mixin(config.mixin);

    return {
      name,
      level: config.level,
      // to disable {pid: process.pid, hostname: os.hostname()} which causes conflicts in DD
      // https://getpino.io/#/docs/api?id=opt-base
      base: undefined,
      mixin,
      ...(config.transport && { transport: config.transport })
    };
  }

  private mixin(mixin?: (mergeObject: object, level: number) => object) {
    return (mergeObject: object, level: number) => {
      const context = mixin ? mixin(mergeObject, level) : {};

      if (this.tracer) {
        const span = this.tracer.scope().active();
        if (span) {
          this.tracer.inject(span.context(), 'log', context);
          const tags = { ...span.context()._tags };
          delete tags['_dd.measured']; // Remove the tag, as its not required to be sent into the log context
          return {
            ...context,
            ...tags
          };
        }
      }

      return context;
    };
  }

  public child(bindings: Record<string, unknown>): Logger {
    return new PinoLogger(this.name, this.config, this.logger.child(bindings));
  }

  public setLogLevel(level: LoggerConfig['level']): void {
    this.logger.level = level;
  }

  public debug = this.makeLogFn('debug');

  public info = this.makeLogFn('info');

  public warn = this.makeLogFn('warn');

  public error = this.makeLogFn('error');

  private makeLogFn(level: 'debug' | 'info' | 'warn' | 'error') {
    return (msg: unknown, obj?: unknown) => {
      if (typeof msg !== 'string') {
        // When `msg` is not a string, we assume that the `msg` is an object to log and `obj` param is ignored
        this.logger[level](msg);
        return;
      }

      if (obj) {
        if (typeof obj === 'object') {
          this.logger[level](obj, msg);
        } else {
          // nest the string inside an object so it can be merged into the json output
          this.logger[level]({ payload: obj }, msg);
        }
      } else {
        this.logger[level](msg);
      }
    };
  }
}
